import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Button,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  Avatar,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Visibility as ViewIcon
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PayrollRegister = () => {
  const theme = useTheme();
  
  // State for month/year selection
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [monthName, setMonthName] = useState("");
  
  // State for data
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({
    totalEmployees: 0,
    totalGrossSalary: 0,
    totalNetSalary: 0,
    totalDeductions: 0,
    totalTax: 0,
    totalPF: 0,
    totalBaseSalary: 0,
    totalHRA: 0,
    totalSpecialAllowance: 0,
    totalBonus: 0,
    totalArrears: 0,
    employeesWithArrear: 0,
    emailsSent: 0
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [costCenterFilter, setCostCenterFilter] = useState("all");
  const [costCenters, setCostCenters] = useState([]);

  // View modal state
  const [viewEmployee, setViewEmployee] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);

  // Update monthName whenever selectedMonth changes
  useEffect(() => {
    setMonthName(months[selectedMonth]);
  }, [selectedMonth, months]);

  // Fetch active Cost Centers for filter dropdown
  useEffect(() => {
    const fetchCostCenters = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios.get(`${API_URL}/api/cost-centers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          setCostCenters(res.data.data || []);
        }
      } catch (err) {
        //console.error("Error fetching cost centers:", err);
      }
    };
    fetchCostCenters();
  }, []);

  // Fetch data when month, year, page, or rowsPerPage changes
  useEffect(() => {
    if (monthName) {
      fetchPayrollRegister();
    }
  }, [selectedMonth, selectedYear, page, rowsPerPage, monthName, departmentFilter, costCenterFilter, searchTerm]);

  const fetchPayrollRegister = async () => {
    if (!monthName) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;


      const response = await axios.get(
        `${API_URL}/api/payslip/register/${monthName}/${selectedYear}`,
        {
          params: {
            
            page: page + 1,
            limit: rowsPerPage,
            search: searchTerm || undefined,
            department: departmentFilter !== 'all' ? departmentFilter : undefined,
            costCenterId: costCenterFilter !== 'all' ? costCenterFilter : undefined
          },
          withCredentials: true,
          headers: {    
              Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setPayrollData(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
        setSummary(response.data.summary || {
          totalEmployees: 0,
          totalGrossSalary: 0,
          totalNetSalary: 0,
          totalDeductions: 0,
          totalTax: 0,
          totalPF: 0,
          totalBaseSalary: 0,
          totalHRA: 0,
          totalSpecialAllowance: 0,
          totalBonus: 0,
          totalArrears: 0,
          employeesWithArrear: 0,
          emailsSent: 0
        });
        
        // Extract unique departments for filter
        const depts = [...new Set((response.data.data || [])
          .map(item => item.department)
          .filter(Boolean))];
        setDepartments(depts);
      }
    } catch (err) {
      //console.error("Error fetching payroll register:", err);
      if (err.response?.status === 404) {
        setError(`No payroll data found for ${monthName} ${selectedYear}. Please generate payslips first.`);
      } else {
        setError(err.response?.data?.message || "Failed to fetch payroll register");
      }
      setPayrollData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    fetchPayrollRegister();
  };

  const handleDepartmentChange = (event) => {
    setDepartmentFilter(event.target.value);
    setPage(0);
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setDepartmentFilter("all");
    setCostCenterFilter("all");
    setPage(0);
    fetchPayrollRegister();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleViewDetails = (employee) => {
    setViewEmployee(employee);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewEmployee(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN').format(value || 0);
  };

  const exportToPDF = () => {

    const doc = new jsPDF('landscape');
    
    // Add title
    doc.setFontSize(18);
    doc.text('Payroll Register', 14, 22);
    doc.setFontSize(11);
    doc.text(`Month: ${monthName} ${selectedYear}`, 14, 30);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 36);
  
    // Add summary - use simple numbers without any formatting first
    doc.setFontSize(10);
    doc.text(`Total Employees: ${summary.totalEmployees}`, 14, 45);
    doc.text(`Total Gross: ${summary.totalGrossSalary}`, 14, 51);
    doc.text(`Total Net: ${summary.totalNetSalary}`, 80, 51);
    doc.text(`Total Deductions: ${summary.totalDeductions}`, 146, 51);
  
    // Table headers
    const tableColumn = [
      "S.No",
      "Employee ID",
      "Employee Name",
      "Cost Center",
      "Department",
      "Designation",
      "Bank Name",
      "Account No",
      "Gross Salary",
      "Deductions",
      "Additionals",
      "Net Salary"
    ];
  
    // Create table rows with RAW data first (no formatting)
    const tableRows = payrollData.map((emp, index) => [
      String(index + 1),
      String(emp.employee_id || 'N/A'),
      String(emp.fullName || 'N/A'),
      String(emp.costCenter || 'Not Assigned'),
      String(emp.department || 'N/A'),
      String(emp.designation || 'N/A'),
      String(emp.bankName || 'N/A'),
      emp.accountNumber ? `XXXX${String(emp.accountNumber).slice(-4)}` : 'N/A',
      String(emp.gross_salary || '0'),
      String(emp.total_deductions || '0'),
      String(emp.additionals || '0'),
      String(emp.net_salary || '0')
    ]);
  
  
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] }
    });
  
    doc.save(`Payroll_Register_${monthName}_${selectedYear}.pdf`);
  };

const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      payrollData.map((emp, index) => ({
        'S.No': index + 1,
        'Employee ID': emp.employee_id,
        'Employee Name': emp.fullName,
        'PAN Number': emp.panNumber || 'N/A',
        'Cost Center': emp.costCenter || 'Not Assigned',
        'Department': emp.department || 'N/A',
        'Designation': emp.designation || 'N/A',
        'Bank Name': emp.bankName || 'N/A',
        'Account Number': emp.accountNumber || 'N/A',
        'IFSC Code': emp.ifscCode || 'N/A',
        'PF Number': emp.pfno || 'N/A',
        'UAN': emp.uan || 'N/A',
        
        // ===== EARNINGS =====
        'Basic Salary': emp.base_salary || 0,
        'HRA': emp.hra || 0,
        'Special Allowance': emp.special_allowance || 0,
        'Variable Salary': emp.variable_salary || 0,
        'Joining Bonus': emp.joining_bonus || 0,
        
        // ===== HR ADJUSTMENTS =====
        'OT Hours': emp.ot_hours || 0,
        'OT Amount': emp.ot_amount || 0,
        'LOP Days': emp.lop_days || 0,
        'LOP Deduction': emp.lop_deduction || 0,
        'Reimbursements': emp.reimbursements || 0,
        'Advance Recovery': emp.advance_recovery || 0,
        
        // ===== GROSS =====
        'Gross Salary': emp.gross_salary || 0,
        
        // ===== DEDUCTIONS =====
        'Employee PF': emp.employeepf || 0,
        'Employer PF': emp.employerpf || 0,
        'VPF': emp.vpf || 0,
        'Professional Tax': emp.professional_tax || 0,
        'Income Tax': emp.monthly_tax || 0,
        'Gratuity': emp.gratuity || 0,
        'Medical Insurance': emp.medicalInsurance || 0,
        'Total Deductions': emp.total_deductions || 0,
        
        // ===== ADDITIONALS =====
        'Arrear Amount': emp.arrear_included || 0,
        'Arrear Source': emp.arrear_source || 'N/A',
        'Has Arrear': emp.has_arrear ? 'Yes' : 'No',
        'Total Additionals': emp.additionals || 0,
        
        // ===== NET =====
        'Net Salary': emp.net_salary || 0,
        'Total Payable (Net + Arrear)': (emp.net_salary || 0) + (emp.arrear_included || 0),
        
        // ===== TAX =====
        'Tax Regime': emp.tax_regime === 'old_regime' ? 'Old Regime' : 'New Regime',
        'Financial Year': emp.financial_year || 'N/A',
        
        // ===== STATUS =====
        'Email Status': emp.email_sent ? 'Sent' : 'Pending',
        'Email Sent Date': emp.email_sent_at ? new Date(emp.email_sent_at).toLocaleDateString() : 'N/A',
        
        // ===== PERIOD =====
        'Month': emp.month,
        'Year': emp.year,
        'Created Date': emp.created_at ? new Date(emp.created_at).toLocaleDateString() : 'N/A'
      }))
    );

    // Set column widths for better readability
    const colWidths = [
        { wch: 8 },   // S.No
        { wch: 15 },  // Employee ID
        { wch: 25 },  // Employee Name
        { wch: 15 },  // PAN Number
        { wch: 20 },  // Cost Center
        { wch: 20 },  // Department
        { wch: 20 },  // Designation
        { wch: 20 },  // Bank Name
        { wch: 18 },  // Account Number
        { wch: 15 },  // IFSC Code
        { wch: 15 },  // PF Number
        { wch: 15 },  // UAN
        { wch: 15 },  // Basic Salary
        { wch: 15 },  // HRA
        { wch: 20 },  // Special Allowance
        { wch: 18 },  // Variable Salary
        { wch: 18 },  // Joining Bonus
        { wch: 12 },  // OT Hours
        { wch: 15 },  // OT Amount
        { wch: 12 },  // LOP Days
        { wch: 18 },  // LOP Deduction
        { wch: 18 },  // Reimbursements
        { wch: 18 },  // Advance Recovery
        { wch: 15 },  // Gross Salary
        { wch: 15 },  // Employee PF
        { wch: 15 },  // Employer PF
        { wch: 15 },  // VPF
        { wch: 18 },  // Professional Tax
        { wch: 15 },  // Income Tax
        { wch: 15 },  // Gratuity
        { wch: 20 },  // Medical Insurance
        { wch: 18 },  // Total Deductions
        { wch: 18 },  // Arrear Amount
        { wch: 18 },  // Arrear Source
        { wch: 12 },  // Has Arrear
        { wch: 18 },  // Total Additionals
        { wch: 15 },  // Net Salary
        { wch: 22 },  // Total Payable
        { wch: 15 },  // Tax Regime
        { wch: 18 },  // Financial Year
        { wch: 15 },  // Email Status
        { wch: 18 },  // Email Sent Date
        { wch: 15 },  // Month
        { wch: 10 },  // Year
        { wch: 15 }   // Created Date
    ];

    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Payroll Register');
    XLSX.writeFile(workbook, `Payroll_Register_${monthName}_${selectedYear}.xlsx`);
};

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
  {/* Left side - Title */}
  <Box>
    <Typography variant="h4" fontWeight="bold">
      Payroll Register
    </Typography>
    <Typography variant="body2" color="textSecondary">
      View and manage employee payroll details
    </Typography>
  </Box>
  
  {/* Right side - Filters */}
  <Box display="flex" gap={2} alignItems="center">
    {/* Month Selector */}
    <TextField
      select
      size="small"
      label="Month"
      value={selectedMonth}
      onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
      sx={{ minWidth: 140 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CalendarIcon fontSize="small" />
          </InputAdornment>
        )
      }}
    >
      {months.map((month, index) => (
        <MenuItem key={month} value={index}>{month}</MenuItem>
      ))}
    </TextField>

    {/* Year Selector */}
    <TextField
      select
      size="small"
      label="Year"
      value={selectedYear}
      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
      sx={{ minWidth: 100 }}
    >
      {years.map((year) => (
        <MenuItem key={year} value={year}>{year}</MenuItem>
      ))}
    </TextField>

    {/* Buttons */}
    <Box display="flex" gap={1}>
      <Button
        variant="contained"
        onClick={fetchPayrollRegister}
        disabled={loading}
        size="medium"
      >
        {loading ? <CircularProgress size={24} /> : 'Load Data'}
      </Button>
      <Tooltip title="Refresh">
        <IconButton onClick={handleRefresh} color="primary" size="small">
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
</Box>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={2}>
  {/* Total Employees - Light Blue */}
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ 
      bgcolor: '#e3f2fd', // Light blue
      color: '#1565c0', // Dark blue text
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }
    }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: '#546e7a', fontSize: '0.7rem' }}>
          TOTAL EMPLOYEES
        </Typography>
        <Typography variant="h4" fontWeight="600" sx={{ mt: 1,fontSize:26 }}>
          {summary.totalEmployees}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  
  {/* Total Gross Salary - Light Green */}
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ 
      bgcolor: '#e8f5e8', // Light green
      color: '#2e7d32', // Dark green text
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }
    }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: '#546e7a', fontSize: '0.7rem' }}>
          TOTAL GROSS SALARY
        </Typography>
        <Typography variant="h6" fontWeight="600" sx={{ mt: 1 }}>
          {formatCurrency(summary.totalGrossSalary)}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  
  {/* Total Deductions - Light Orange/Peach */}
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ 
      bgcolor: '#fff3e0', // Light orange/peach
      color: '#e65100', // Dark orange text
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }
    }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: '#546e7a', fontSize: '0.7rem' }}>
          TOTAL DEDUCTIONS
        </Typography>
        <Typography variant="h6" fontWeight="600" sx={{ mt: 1 }}>
          {formatCurrency(summary.totalDeductions)}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  
  {/* Total Net Salary - Light Purple */}
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ 
      bgcolor: '#f3e5f5', // Light purple
      color: '#6a1b9a', // Dark purple text
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }
    }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: '#546e7a', fontSize: '0.7rem' }}>
          TOTAL NET SALARY
        </Typography>
        <Typography variant="h6" fontWeight="600" sx={{ mt: 1 }}>
          {formatCurrency(summary.totalNetSalary)}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
</Grid>
        </Paper>

        {/* Search and Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { setSearchTerm(""); handleSearch(); }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  onChange={handleDepartmentChange}
                  label="Department"
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Cost Center</InputLabel>
                <Select
                  value={costCenterFilter}
                  onChange={(e) => { setCostCenterFilter(e.target.value); setPage(0); }}
                  label="Cost Center"
                >
                  <MenuItem value="all">All Cost Centers</MenuItem>
                  {costCenters.map(cc => (
                    <MenuItem key={cc.id} value={cc.id.toString()}>{cc.name} ({cc.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={exportToExcel}
                  disabled={payrollData.length === 0 || loading}
                >
                  Excel
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={exportToPDF}
                  disabled={payrollData.length === 0 || loading}
                >
                  PDF
                </Button>
             
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }} 
            onClose={() => setError(null)}
            action={
              <Button color="inherit" size="small" onClick={fetchPayrollRegister}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Payroll Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }}>Employee Details</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }}>Cost Center</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }}>Designation</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }}>Bank Details</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }} align="right">Gross Salary</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }} align="right">Deductions</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }} align="right">Additionals</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }} align="right">Net Salary</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[50] }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                        Loading payroll data...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : payrollData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                      <ReceiptIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
                      <Typography variant="h6" color="textSecondary">
                        No payroll data found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {error ? error : `No payslips generated for ${monthName} ${selectedYear}`}
                      </Typography>
                    
                    </TableCell>
                  </TableRow>
                ) : (
                  payrollData.map((emp, index) => (
                    <TableRow 
                      key={emp.employee_id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Chip
                          label={emp.employee_id}
                          size="small"
                          variant="outlined"
                        />
                                                <Box display="flex" alignItems="center">

                         <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: theme.palette.primary.main }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                         <Typography variant="body2" fontWeight="medium">
                              {emp.fullName}
                            </Typography>
                            </Box>
                      </TableCell>
                    
                      
                      <TableCell>{emp.costCenter || 'Not Assigned'}</TableCell>
                      <TableCell>{emp.department || 'N/A'}</TableCell>
                      <TableCell>{emp.designation || 'N/A'}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="caption" display="block" fontWeight="medium">
                            {emp.bankName || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {emp.accountNumber ? `A/C: XXXX${emp.accountNumber.slice(-4)}` : 'N/A'}
                          </Typography>
                          {emp.ifscCode && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              IFSC: {emp.ifscCode}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {formatCurrency(emp.gross_salary)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="error.main">
                          {formatCurrency(emp.total_deductions)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="error.main">
                          {formatCurrency(emp.additionals)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="success.main">
                          {formatCurrency(emp.net_salary)}
                        </Typography>
                        {emp.arrear_included > 0 && (
                          <Chip
                            label={`+${formatCurrency(emp.arrear_included)}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ mt: 0.5, height: 20 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleViewDetails(emp)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                     
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {payrollData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount || payrollData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
            />
          )}
        </Paper>

       

{/* View Details Modal */}
<Dialog 
  open={viewModalOpen} 
  onClose={handleCloseViewModal}
  maxWidth="md"
  fullWidth
>
  <DialogTitle>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="h6">Employee Payslip Details</Typography>
      <IconButton onClick={handleCloseViewModal} size="small">
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>
  <DialogContent dividers>
    {viewEmployee && (
      <Grid container spacing={3}>
        {/* Employee Info */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Employee Information
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.grey[50] }}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Employee ID</Typography>
                <Typography variant="body2">{viewEmployee.employee_id}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Name</Typography>
                <Typography variant="body2">{viewEmployee.fullName}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">PAN</Typography>
                <Typography variant="body2">{viewEmployee.panNumber}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">PF/UAN</Typography>
                <Typography variant="body2">PF: {viewEmployee.pfno}<br/>UAN: {viewEmployee.uan}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Department</Typography>
                <Typography variant="body2">{viewEmployee.department}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Designation</Typography>
                <Typography variant="body2">{viewEmployee.designation}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Cost Center</Typography>
                <Typography variant="body2">{viewEmployee.costCenter || 'Not Assigned'}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" color="textSecondary">Bank Details</Typography>
                <Typography variant="body2">
                  {viewEmployee.bankName} - A/C: XXXX{viewEmployee.accountNumber?.slice(-4)} | IFSC: {viewEmployee.ifscCode}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Salary Breakdown */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Salary Breakdown - {viewEmployee.month} {viewEmployee.year}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Earnings</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Base Salary</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.base_salary)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">HRA</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.hra)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Special Allowance</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.special_allowance)}</Typography>
                  </Box>

                  {viewEmployee.variable_salary > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Variable Salary</Typography>
                      <Typography variant="body2">{formatCurrency(viewEmployee.variable_salary)}</Typography>
                    </Box>
                  )}
                  {viewEmployee.joining_bonus > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Joining Bonus</Typography>
                      <Typography variant="body2">{formatCurrency(viewEmployee.joining_bonus)}</Typography>
                    </Box>
                  )}
                  {viewEmployee.ot_amount > 0 && (
                    <Box display="flex" justifyContent="space-between" sx={{ color: 'info.main' }}>
                      <Typography variant="body2">OT Amount ({viewEmployee.ot_hours || 0} hrs)</Typography>
                      <Typography variant="body2">{formatCurrency(viewEmployee.ot_amount)}</Typography>
                    </Box>
                  )}
            
                  {viewEmployee.lop_deduction > 0 && (
                    <Box display="flex" justifyContent="space-between" sx={{ color: 'error.main' }}>
                      <Typography variant="body2">Less: LOP ({viewEmployee.lop_days || 0} days)</Typography>
                      <Typography variant="body2">-{formatCurrency(viewEmployee.lop_deduction)}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" fontWeight="bold">
                    <Typography variant="body2">Gross Salary</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.gross_salary)}</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>Deductions</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Employee PF</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.employeepf)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Employer PF</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.employerpf)}</Typography>
                  </Box>
                  {viewEmployee.vpf > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">VPF</Typography>
                      <Typography variant="body2">{formatCurrency(viewEmployee.vpf)}</Typography>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Professional Tax</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.professional_tax)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Income Tax</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.monthly_tax)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Gratuity</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.gratuity)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Medical Insurance</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.medicalInsurance)}</Typography>
                  </Box>
                  {viewEmployee.advance_recovery > 0 && (
                    <Box display="flex" justifyContent="space-between" sx={{ color: 'error.main' }}>
                      <Typography variant="body2">Advance Recovery</Typography>
                      <Typography variant="body2">{formatCurrency(viewEmployee.advance_recovery)}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" fontWeight="bold" color="error.main">
                    <Typography variant="body2">Total Deductions</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.total_deductions)}</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Additionals Section */}
        {(viewEmployee.reimbursements > 0 || viewEmployee.arrear_included > 0) && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="info.main">
              Additionals
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {viewEmployee.reimbursements > 0 && (
                  <Box display="flex" justifyContent="space-between" sx={{ color: 'info.main' }}>
                    <Typography variant="body2">Reimbursements</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.reimbursements)}</Typography>
                  </Box>
                )}
                {viewEmployee.has_arrear && (
                  <Box display="flex" justifyContent="space-between" sx={{ color: 'warning.main' }}>
                    <Typography variant="body2">Arrear ({viewEmployee.arrear_source || 'from previous months'})</Typography>
                    <Typography variant="body2">{formatCurrency(viewEmployee.arrear_included)}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between" fontWeight="bold" color="info.main">
                  <Typography variant="body2">Total Additionals</Typography>
                  <Typography variant="body2">{formatCurrency(viewEmployee.additionals || 0)}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Tax Summary */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Tax Summary
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.primary.light }}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Regime</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {viewEmployee.tax_regime === 'old_regime' ? 'Old Regime' : 'New Regime'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Financial Year</Typography>
                <Typography variant="body2" fontWeight="bold">{viewEmployee.financial_year}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Monthly Tax</Typography>
                <Typography variant="body2" fontWeight="bold">{formatCurrency(viewEmployee.monthly_tax)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Total PF</Typography>
                <Typography variant="body2" fontWeight="bold">{formatCurrency(viewEmployee.total_pf)}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Net Salary Summary */}
        <Grid item xs={12}>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              bgcolor: theme.palette.success.light,
              color: 'white'
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="body2">Net Salary</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(viewEmployee.net_salary)}
                </Typography>
              </Grid>
              {viewEmployee.has_arrear && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2">Arrear Included</Typography>
                  <Typography variant="h6">
                    + {formatCurrency(viewEmployee.arrear_included)}
                  </Typography>
                  <Typography variant="caption">from {viewEmployee.arrear_source}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <Typography variant="body2">Total Payable</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency((viewEmployee.net_salary || 0) + (viewEmployee.arrear_included || 0))}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Email Status */}
        <Grid item xs={12}>
          <Typography variant="caption" color="textSecondary">
            Email Status: {viewEmployee.email_status || 'pending'} 
            {viewEmployee.email_sent_at && ` | Sent on: ${new Date(viewEmployee.email_sent_at).toLocaleDateString()}`}
          </Typography>
        </Grid>
      </Grid>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCloseViewModal}>Close</Button>
  </DialogActions>
</Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PayrollRegister;